import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

function useDebouncedCallback(fn, delay) {
  const t = useRef(null);
  return useMemo(() => {
    return (...args) => {
      if (t.current) clearTimeout(t.current);
      t.current = setTimeout(() => fn(...args), delay);
    };
  }, [fn, delay]);
}

export default function SharedPanel({ sessionCode, playerId, onNewShared }) {
  const API = process.env.REACT_APP_API_URL;

  const [text, setText] = useState("");
  const [remoteUpdatedAt, setRemoteUpdatedAt] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const isEditingRef = useRef(false);
  const dirtyRef = useRef(false);
  const lastAppliedRemoteAtRef = useRef(null);
  const lastNotifiedRemoteAtRef = useRef(null);

  const fetchRemote = async () => {
    try {
      const res = await axios.get(`${API}/api/sessions/${sessionCode}/shared-notes`);
      const nextText = res.data?.text ?? "";
      const nextUpdatedAt = res.data?.updatedAt ?? null;
      if (isEditingRef.current || dirtyRef.current) { setRemoteUpdatedAt(nextUpdatedAt); return; }
      if (nextUpdatedAt && lastAppliedRemoteAtRef.current === nextUpdatedAt) return;
      setText(nextText);
      setRemoteUpdatedAt(nextUpdatedAt);
      lastAppliedRemoteAtRef.current = nextUpdatedAt;
      if (nextUpdatedAt && lastNotifiedRemoteAtRef.current !== nextUpdatedAt) {
        lastNotifiedRemoteAtRef.current = nextUpdatedAt;
        onNewShared?.(1);
      }
    } catch { }
  };

  const saveRemote = async (nextText) => {
    setStatus("saving"); setError("");
    try {
      const res = await axios.patch(`${API}/api/sessions/${sessionCode}/shared-notes`, { playerId, text: nextText });
      dirtyRef.current = false;
      setStatus("saved");
      const srvUpdatedAt = res.data?.updatedAt ?? null;
      setRemoteUpdatedAt(srvUpdatedAt);
      lastAppliedRemoteAtRef.current = srvUpdatedAt;
      setTimeout(() => setStatus(s => s === "saved" ? "idle" : s), 900);
    } catch (e) {
      setStatus("error");
      setError(e?.response?.data?.message || "Sauvegarde impossible");
    }
  };

  const debouncedSave = useDebouncedCallback(saveRemote, 500);

  useEffect(() => {
    fetchRemote();
    const itv = setInterval(fetchRemote, 2000);
    return () => clearInterval(itv);
  }, [sessionCode]);

  const statusColor = status === "saving" ? "rgba(251,191,36,.8)" : status === "saved" ? "rgba(52,211,153,.8)" : status === "error" ? "rgba(239,68,68,.8)" : "rgba(100,116,139,.5)";
  const statusLabel = status === "saving" ? "⏳ sync…" : status === "saved" ? "✓ sauvegardé" : status === "error" ? "⚠ erreur" : remoteUpdatedAt ? "● sync" : "";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, height: "100%" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(148,163,184,.8)", fontFamily: "ui-monospace,monospace", fontWeight: 700 }}>
          Notes partagées
        </div>
        <span style={{ fontSize: 10, color: statusColor, fontFamily: "ui-monospace,monospace", fontWeight: 600 }}>
          {statusLabel}
        </span>
      </div>
      <div style={{ fontSize: 11, color: "rgba(148,163,184,.6)", fontFamily: "ui-sans-serif,system-ui", marginTop: -4 }}>
        Visible par toute l'équipe
      </div>

      {/* Textarea */}
      <textarea
        value={text}
        onFocus={e => { isEditingRef.current = true; e.target.style.borderColor = "rgba(99,102,241,.45)"; e.target.style.boxShadow = "0 0 0 2px rgba(99,102,241,.12)"; }}
        onBlur={e => { isEditingRef.current = false; if (dirtyRef.current) saveRemote(text); e.target.style.borderColor = "rgba(255,255,255,.08)"; e.target.style.boxShadow = "none"; }}
        onChange={e => { const v = e.target.value; setText(v); dirtyRef.current = true; debouncedSave(v); }}
        placeholder={"· Infos à partager\n· Ce que tu as trouvé\n· Hypothèses d'équipe"}
        style={{
          flex: 1, minHeight: 180, resize: "none",
          borderRadius: 8,
          background: "rgba(0,0,0,.45)",
          border: "1px solid rgba(255,255,255,.08)",
          padding: "10px 12px",
          fontSize: 12, lineHeight: 1.55,
          color: "rgba(226,232,240,.9)",
          fontFamily: "ui-sans-serif,system-ui",
          outline: "none",
        }}
        className="custom-scrollbar placeholder:text-slate-600"
      />

      {error && (
        <div style={{ borderRadius: 7, border: "1px solid rgba(239,68,68,.25)", background: "rgba(239,68,68,.08)", padding: "6px 10px", fontSize: 11, color: "rgba(252,165,165,.85)", fontFamily: "ui-sans-serif,system-ui" }}>
          {error}
        </div>
      )}
    </div>
  );
}